// A Pricing Rate Card API
import {
    APricingRateCardFilterRequestResponse,
    APricingRateCardResponse,
    StandardAdditionalFeeFilterRequestResponse,
    APricingStandardBaseLeaseFilterRequestResponse,
} from 'app/requests/APricingRateCard/APricingRateCard.types'
import {
    RequestConfigFunction,
} from 'app/types/request.types'
import {
    RateCardStatus,
} from 'app/enums/label.enums'
import {
    WithId,
} from 'app/types/common.types'

const rateCardStatusToEndpoint: Record<RateCardStatus, string> = {
    [RateCardStatus.IN_PROGRESS]: 'inprogress',
    [RateCardStatus.VALID]: 'valid',
    [RateCardStatus.CANCELLED]: 'cancelled',
}

type APricingRateCardRequests = {
    filterRequest: RequestConfigFunction<
        {
            params?: Record<string, unknown>
        },
        APricingRateCardFilterRequestResponse
    >
    getById: RequestConfigFunction<WithId<number>, APricingRateCardResponse>
    filterRequestCount: RequestConfigFunction<{ fieldName: string }>
    create: RequestConfigFunction<undefined>
    update: RequestConfigFunction<WithId<number>>
    setStatus: RequestConfigFunction<{ id: number; status: RateCardStatus }>
    createStandardBaseLeaseRateCard: RequestConfigFunction<{
        rateCardId: number
    }>
    createStandardBaseLeaseRateCardBulk: RequestConfigFunction<{
        rateCardId: number
    }>
    createStandardAdditionalFeeRateCard: RequestConfigFunction<{
        rateCardId: number
    }>
    updateStandardBaseLeaseRateCardBulk: RequestConfigFunction<{
        rateCardId: number
    }>
    updateStandardAdditionalFeeRateCardBulk: RequestConfigFunction<{
        rateCardId: number
    }>
    standardBaseLeaseFilterRequest: RequestConfigFunction<
        {
            rateCardId: number
            params?: Record<string, unknown>
        },
        APricingStandardBaseLeaseFilterRequestResponse
    >
    standardBaseLeaseFilterRequestCount: RequestConfigFunction<{
        rateCardId: number
        fieldName: string
    }>
    standardAdditionalFeeFilterRequest: RequestConfigFunction<
        {
            rateCardId: number
            params?: Record<string, unknown>
        },
        StandardAdditionalFeeFilterRequestResponse
    >
    standardAdditionalFeeFilterRequestCount: RequestConfigFunction<{
        rateCardId: number
        fieldName: string
    }>
}

const aPricingRateCardRequests: APricingRateCardRequests = {
    filterRequest: ({
        params,
    } = {}) => {
        return {
            url: 'apricingratecard/filter-request/all',
            method: 'POST',
            ...params,
        }
    },
    filterRequestCount: ({
        fieldName,
    }) => {
        return {
            url: `apricingratecard/filter-request/all/count/${fieldName}`,
            method: 'POST',
        }
    },
    getById: ({
        id,
    }) => {
        return {
            url: `apricingratecard/${id}`,
            method: 'GET',
        }
    },
    create: () => {
        return {
            url: 'apricingratecard',
            method: 'POST',
        }
    },
    update: ({
        id,
    }) => {
        return {
            url: `apricingratecard/${id}`,
            method: 'PATCH',
        }
    },
    setStatus: ({
        id, status,
    }) => {
        return {
            url: `apricingratecard/${id}/${rateCardStatusToEndpoint[status]}`,
            method: 'PATCH',
        }
    },
    createStandardBaseLeaseRateCard: ({
        rateCardId,
    }) => {
        return {
            url: `apricingratecard/${rateCardId}/standardbaseleaseratecard`,
            method: 'POST',
        }
    },
    createStandardBaseLeaseRateCardBulk: ({
        rateCardId,
    }) => {
        return {
            url: `apricingratecard/${rateCardId}/standardbaseleaseratecard/bulk`,
            method: 'POST',
        }
    },
    createStandardAdditionalFeeRateCard: ({
        rateCardId,
    }) => {
        return {
            url: `apricingratecard/${rateCardId}/standardadditionalfeeratecard`,
            method: 'POST',
        }
    },
    updateStandardBaseLeaseRateCardBulk: ({
        rateCardId,
    }) => {
        return {
            url: `apricingratecard/${rateCardId}/standardbaseleaseratecard/bulk`,
            method: 'PATCH',
        }
    },
    updateStandardAdditionalFeeRateCardBulk: ({
        rateCardId,
    }) => {
        return {
            url: `apricingratecard/${rateCardId}/standardadditionalfeeratecard/bulk`,
            method: 'PATCH',
        }
    },
    standardBaseLeaseFilterRequest: (
        {
            rateCardId, params,
        } = {} as {
            rateCardId: number
            params?: Record<string, unknown>
        },
    ) => {
        return {
            url: `apricingratecard/${rateCardId}/standardbaseleaseratecard/filter-request/all`,
            method: 'POST',
            ...params,
        }
    },
    standardBaseLeaseFilterRequestCount: ({
        rateCardId, fieldName,
    }) => {
        return {
            url: `apricingratecard/${rateCardId}/standardbaseleaseratecard/filter-request/all/count/${fieldName}`,
            method: 'POST',
        }
    },
    standardAdditionalFeeFilterRequest: (
        {
            rateCardId, params,
        } = {} as {
            rateCardId: number
            params?: Record<string, unknown>
        },
    ) => {
        return {
            url: `apricingratecard/${rateCardId}/standardadditionalfeeratecard/filter-request/all`,
            method: 'POST',
            ...params,
        }
    },
    standardAdditionalFeeFilterRequestCount: ({
        rateCardId, fieldName,
    }) => {
        return {
            url: `apricingratecard/${rateCardId}/standardadditionalfeeratecard/filter-request/all/count/${fieldName}`,
            method: 'POST',
        }
    },
}

export default aPricingRateCardRequests
